import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import useClaimsReadySnapshot from '../hooks/useClaimsReadySnapshot';
import Shepherd from 'shepherd.js';
import { getTourSteps } from '../tour/tourSteps';
import 'shepherd.js/dist/css/shepherd.css';
import '../tour/shepherd-theme-traklog.css';

const TourContext = createContext();

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

export const TourProvider = ({ children }) => {
  // AuthContext exposes `currentUser`, `subscriberId` and `subscriberRole`.
  // This used to read `user` and `subscriberInfo`, keys AuthContext has never
  // had, so every guard below returned early: the tour state never loaded, the
  // Tasks page auto-start never fired, and Settings, User Guide, "Start the
  // tour" reset nothing and started nothing. The same mistake silenced the
  // onboarding checklist (see OnboardingContext).
  const { currentUser: user, subscriberId, subscriberRole } = useAuth();
  const [tourCompleted, setTourCompleted] = useState(null); // null = loading, true/false = loaded
  // Set by resetTour, i.e. someone asked for the tour. Kept apart from
  // `tourCompleted`, which is also false for anyone who simply never took it:
  // an established workspace's admin has never taken it either, and must not
  // have it start by itself (see the auto-start in App.js).
  const [tourRequested, setTourRequested] = useState(false);
  const [tourActive, setTourActive] = useState(false);
  const tourRef = useRef(null);
  const firestoreLoadedRef = useRef(false);

  // Load tour state from Firestore (same doc as onboarding). Until it has
  // been read the tour counts as "not loaded" (null).
  useEffect(() => {
    setTourCompleted(null);
    setTourRequested(false);
    firestoreLoadedRef.current = false;
  }, [user?.uid, subscriberId]);

  // Waits for the token to carry the workspace: a listener started before
  // that is refused, and its error handler would record the tour as never
  // taken. A refusal anyway is listened to again (useClaimsReadySnapshot).
  const { status: tourStatus } = useClaimsReadySnapshot(
    (sid) => (user?.uid ? doc(db, 'subscribers', sid, 'user_onboarding', user.uid) : null),
    {
      subscriberId: subscriberId || null,
      label: 'Tour state',
      onNext: (docSnap) => {
        firestoreLoadedRef.current = true;
        if (docSnap.exists()) {
          const data = docSnap.data();
          setTourCompleted(data.tourCompleted || false);
          setTourRequested(data.tourRequested === true);
        } else {
          // New user — tour not completed
          setTourCompleted(false);
          setTourRequested(false);
        }
      },
      onError: (error) => {
        console.error('Error loading tour state:', error);
        firestoreLoadedRef.current = true;
        setTourCompleted(false);
      },
    },
    [user?.uid || null]
  );

  useEffect(() => {
    if (tourStatus !== 'idle') return;
    setTourCompleted(null);
    setTourRequested(false);
    firestoreLoadedRef.current = false;
  }, [tourStatus]);

  // Clean up tour instance on unmount
  useEffect(() => {
    return () => {
      if (tourRef.current) {
        tourRef.current.cancel();
        tourRef.current = null;
      }
    };
  }, []);

  const saveTourState = useCallback(async (completed) => {
    if (!user?.uid || !subscriberId) return;

    const onboardingDocRef = doc(db, 'subscribers', subscriberId, 'user_onboarding', user.uid);

    try {
      await setDoc(onboardingDocRef, {
        tourCompleted: completed,
        // Taken, or waved away: either way the request has been answered.
        tourRequested: false,
        ...(completed ? { tourCompletedAt: new Date().toISOString() } : {}),
        userId: user.uid,
        userEmail: user.email
      }, { merge: true });
    } catch (error) {
      console.error('Error saving tour state:', error);
    }
  }, [user?.uid, user?.email, subscriberId]);

  const buildProgressHTML = useCallback((currentIndex, totalSteps) => {
    const dots = Array.from({ length: totalSteps }, (_, i) => {
      let cls = 'shepherd-progress-dot';
      if (i === currentIndex) cls += ' shepherd-progress-dot--active';
      else if (i < currentIndex) cls += ' shepherd-progress-dot--completed';
      return `<span class="${cls}"></span>`;
    }).join('');
    return `<span class="shepherd-progress-dots">${dots}</span>`;
  }, []);

  const startTour = useCallback(() => {
    // Don't start if already active
    if (tourRef.current?.isActive()) return;

    const role = subscriberRole || localStorage.getItem('subscriberRole') || 'user';
    const steps = getTourSteps(role);

    // Filter steps whose showOn returns false
    const visibleSteps = steps.filter(step => {
      if (typeof step.showOn === 'function') return step.showOn();
      return true;
    });

    if (visibleSteps.length === 0) return;

    const tour = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        cancelIcon: { enabled: true },
        scrollTo: { behavior: 'smooth', block: 'center' },
        modalOverlayOpeningPadding: 8,
        modalOverlayOpeningRadius: 8
      }
    });

    // Add steps with proper button actions and progress dots
    visibleSteps.forEach((stepDef, index) => {
      const { buttons, showOn, ...stepConfig } = stepDef;

      // Build buttons with resolved actions and progress dots
      const resolvedButtons = [];

      // Add progress dots as first "button" (left side)
      resolvedButtons.push({
        text: buildProgressHTML(index, visibleSteps.length),
        classes: 'shepherd-step-counter',
        disabled: true,
        action: () => {} // no-op, just a display element
      });

      // Add actual navigation buttons
      buttons.forEach(btn => {
        resolvedButtons.push({
          text: btn.text,
          classes: btn.classes || '',
          action: () => {
            if (btn.action === 'next') tour.next();
            else if (btn.action === 'back') tour.back();
            else if (btn.action === 'cancel') tour.cancel();
          }
        });
      });

      tour.addStep({
        ...stepConfig,
        buttons: resolvedButtons
      });
    });

    tour.on('complete', () => {
      setTourActive(false);
      setTourCompleted(true);
      setTourRequested(false);
      saveTourState(true);
      tourRef.current = null;
    });

    tour.on('cancel', () => {
      setTourActive(false);
      // Mark as completed on cancel too — don't nag users
      setTourCompleted(true);
      setTourRequested(false);
      saveTourState(true);
      tourRef.current = null;
    });

    tourRef.current = tour;
    setTourActive(true);
    tour.start();
  }, [subscriberRole, saveTourState, buildProgressHTML]);

  const resetTour = useCallback(async () => {
    if (!user?.uid || !subscriberId) return;

    const onboardingDocRef = doc(db, 'subscribers', subscriberId, 'user_onboarding', user.uid);

    try {
      await setDoc(onboardingDocRef, {
        tourCompleted: false,
        tourCompletedAt: null,
        tourRequested: true,
        userId: user.uid,
        userEmail: user.email
      }, { merge: true });
      setTourCompleted(false);
      setTourRequested(true);
    } catch (error) {
      console.error('Error resetting tour:', error);
    }
  }, [user?.uid, user?.email, subscriberId]);

  /**
   * Point at one element: the getting-started checklist's "Show me".
   *
   * The caller has usually just navigated, and the settings pages are lazy
   * loaded, so the element may not exist yet. This waits for it (up to
   * `timeoutMs`) and then shows a single Shepherd step on it. If it never
   * appears nothing is shown, which is better than a popover pointing at the
   * middle of an empty screen. Never touches `tourCompleted`: pointing at a
   * button is not the tour.
   *
   * Resolves true if it showed something.
   */
  const spotlight = useCallback((target, { title, text, timeoutMs = 5000 } = {}) => {
    const selector = `[data-tour="${target}"]`;
    if (tourRef.current?.isActive()) tourRef.current.cancel();

    return new Promise((resolve) => {
      const started = Date.now();
      const attempt = () => {
        const element = document.querySelector(selector);
        if (!element) {
          if (Date.now() - started >= timeoutMs) {
            resolve(false);
            return;
          }
          setTimeout(attempt, 100);
          return;
        }

        const tour = new Shepherd.Tour({
          useModalOverlay: true,
          defaultStepOptions: {
            cancelIcon: { enabled: true },
            scrollTo: { behavior: 'smooth', block: 'center' },
            modalOverlayOpeningPadding: 6,
            modalOverlayOpeningRadius: 8,
          },
        });
        tour.addStep({
          id: `spotlight-${target}`,
          title,
          text,
          attachTo: { element: selector, on: 'bottom' },
          // Clicking the highlighted element itself does what it says and
          // closes the popover, so "Show me" leads straight into doing it.
          advanceOn: { selector, event: 'click' },
          buttons: [{ text: 'Got it', action: () => tour.complete() }],
        });
        const finish = () => {
          setTourActive(false);
          if (tourRef.current === tour) tourRef.current = null;
        };
        tour.on('complete', finish);
        tour.on('cancel', finish);

        tourRef.current = tour;
        setTourActive(true);
        tour.start();
        resolve(true);
      };
      attempt();
    });
  }, []);

  const value = {
    tourCompleted,
    tourRequested,
    tourActive,
    startTour,
    resetTour,
    spotlight
  };

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
};

export default TourContext;
